import * as THREE from 'three';
import {
  BLOCK_TRANSFORM_MIRROR_X,
  BLOCK_TRANSFORM_MIRROR_Y,
  CRAFT_GRID_SIZE,
  isVoxelGlowing,
  voxelMaterialOpacity,
  type VoxelMaterial,
  type CustomBlockCuboid,
  type BlockNoiseParams,
} from '@shared/protocol';
import {
  applyBlockNoiseRgb,
  isNoiseActive,
} from '@shared/blockNoise';
import { blockTypeById } from './blocks';

export type MicroVoxelTuple = [number, VoxelMaterial];

export interface MicroVoxelGeometries {
  opaque: THREE.BufferGeometry;
  transparent: THREE.BufferGeometry;
  /** Self-lit (MeshBasicMaterial) glow voxels — no scene lighting needed. */
  glowing: THREE.BufferGeometry;
}

const FACES = [
  { d: [1, 0, 0], n: [1, 0, 0], c: [[1, 0, 0], [1, 1, 0], [1, 1, 1], [1, 0, 1]] },
  { d: [-1, 0, 0], n: [-1, 0, 0], c: [[0, 0, 1], [0, 1, 1], [0, 1, 0], [0, 0, 0]] },
  { d: [0, 1, 0], n: [0, 1, 0], c: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]] },
  { d: [0, -1, 0], n: [0, -1, 0], c: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]] },
  { d: [0, 0, 1], n: [0, 0, 1], c: [[1, 0, 1], [1, 1, 1], [0, 1, 1], [0, 0, 1]] },
  { d: [0, 0, -1], n: [0, 0, -1], c: [[0, 0, 0], [0, 1, 0], [1, 1, 0], [1, 0, 0]] },
] as const;

export function microVoxelIndex(
  x: number,
  y: number,
  z: number,
  gridSize = CRAFT_GRID_SIZE,
): number {
  return x + z * gridSize + y * gridSize * gridSize;
}

export function unpackMicroVoxel(
  index: number,
  gridSize = CRAFT_GRID_SIZE,
): [number, number, number] {
  const y = Math.floor(index / (gridSize * gridSize));
  const rest = index - y * gridSize * gridSize;
  const z = Math.floor(rest / gridSize);
  return [rest - z * gridSize, y, z];
}

/** Apply a placed custom block's persisted rotation/mirror marker. */
export function transformMicroVoxels(
  voxels: MicroVoxelTuple[],
  gridSize: number,
  transform: number,
): MicroVoxelTuple[] {
  const rotateX = transform & 0x03;
  const rotateY = (transform >> 2) & 0x03;
  const rotateZ = (transform >> 6) & 0x03;
  const mirrorX = (transform & BLOCK_TRANSFORM_MIRROR_X) !== 0;
  const mirrorY = (transform & BLOCK_TRANSFORM_MIRROR_Y) !== 0;
  return voxels.map(([index, type]) => {
    let [x, y, z] = unpackMicroVoxel(index, gridSize);
    for (let turn = 0; turn < rotateX; turn++) {
      [y, z] = [gridSize - 1 - z, y];
    }
    for (let turn = 0; turn < rotateY; turn++) {
      [x, z] = [z, gridSize - 1 - x];
    }
    for (let turn = 0; turn < rotateZ; turn++) {
      [x, y] = [gridSize - 1 - y, x];
    }
    if (mirrorX) x = gridSize - 1 - x;
    if (mirrorY) y = gridSize - 1 - y;
    return [microVoxelIndex(x, y, z, gridSize), type];
  });
}

export function isVoxelOpaque(material: VoxelMaterial): boolean {
  if (typeof material === 'number') return true;
  return voxelMaterialOpacity(material) >= 1;
}

export function isEmptyGeometry(geometry: THREE.BufferGeometry): boolean {
  return (geometry.getAttribute('position')?.count ?? 0) === 0;
}

/**
 * Build exposed-face meshes for a sparse voxel definition.
 * Opaque and transparent parts are separate so glass does not depth-occlude frames.
 * Transparent neighbors do not hide opaque faces (and vice versa).
 */
export function buildMicroVoxelGeometries(
  voxels: MicroVoxelTuple[],
  gridSize = CRAFT_GRID_SIZE,
  noise: BlockNoiseParams | null = null,
  noiseSeed = 0,
): MicroVoxelGeometries {
  const cells = new Map(voxels);
  return {
    opaque: buildNaiveGeometry(cells, gridSize, 'opaque', noise, noiseSeed),
    transparent: buildNaiveGeometry(cells, gridSize, 'transparent', noise, noiseSeed),
    glowing: buildNaiveGeometry(cells, gridSize, 'glowing', noise, noiseSeed),
  };
}

/** @deprecated Prefer buildMicroVoxelGeometries for mixed opacity. */
export function buildMicroVoxelGeometry(
  voxels: MicroVoxelTuple[],
  gridSize = CRAFT_GRID_SIZE,
): THREE.BufferGeometry {
  return mergeGeometries(buildMicroVoxelGeometries(voxels, gridSize));
}

/** Build greedy exposed-face geometry from the saved cuboid representation. */
export function buildOptimizedMicroVoxelGeometries(
  cuboids: CustomBlockCuboid[],
  gridSize = CRAFT_GRID_SIZE,
  noise: BlockNoiseParams | null = null,
  noiseSeed = 0,
): MicroVoxelGeometries {
  const cells = new Map<number, VoxelMaterial>();
  for (const cuboid of cuboids) {
    for (let y = cuboid.y; y < cuboid.y + cuboid.height; y++) {
      for (let z = cuboid.z; z < cuboid.z + cuboid.depth; z++) {
        for (let x = cuboid.x; x < cuboid.x + cuboid.width; x++) {
          cells.set(microVoxelIndex(x, y, z, gridSize), cuboid.material);
        }
      }
    }
  }
  // Per-voxel noise needs unique colors — fall back to naive meshing.
  if (isNoiseActive(noise)) {
    return buildMicroVoxelGeometries([...cells.entries()], gridSize, noise, noiseSeed);
  }
  // Split occupancy so the other opacity class counts as air for face culling.
  // Opaque frames keep faces against glass; glass keeps faces against frames.
  // Glowing voxels are meshed separately (BasicMaterial) and cull like opaque.
  return {
    opaque: buildGreedyGeometry(filterByClass(cells, 'opaque'), gridSize),
    transparent: buildGreedyGeometry(filterByClass(cells, 'transparent'), gridSize),
    glowing: buildGreedyGeometry(filterByClass(cells, 'glowing'), gridSize),
  };
}

/** @deprecated Prefer buildOptimizedMicroVoxelGeometries for mixed opacity. */
export function buildOptimizedMicroVoxelGeometry(
  cuboids: CustomBlockCuboid[],
  gridSize = CRAFT_GRID_SIZE,
): THREE.BufferGeometry {
  return mergeGeometries(buildOptimizedMicroVoxelGeometries(cuboids, gridSize));
}

interface GreedyMaskCell {
  material: VoxelMaterial;
  normal: 1 | -1;
}

type VoxelRenderClass = 'opaque' | 'transparent' | 'glowing';

function voxelRenderClass(material: VoxelMaterial): VoxelRenderClass {
  if (isVoxelGlowing(material)) return 'glowing';
  return isVoxelOpaque(material) ? 'opaque' : 'transparent';
}

function filterByClass(
  cells: ReadonlyMap<number, VoxelMaterial>,
  mode: VoxelRenderClass,
): Map<number, VoxelMaterial> {
  const filtered = new Map<number, VoxelMaterial>();
  for (const [index, material] of cells) {
    if (voxelRenderClass(material) === mode) filtered.set(index, material);
  }
  return filtered;
}

function buildNaiveGeometry(
  cells: ReadonlyMap<number, VoxelMaterial>,
  gridSize: number,
  mode: VoxelRenderClass,
  noise: BlockNoiseParams | null = null,
  noiseSeed = 0,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  for (const [index, type] of cells) {
    if (voxelRenderClass(type) !== mode) continue;
    const [x, y, z] = unpackMicroVoxel(index, gridSize);
    const [red, green, blue, alpha] = voxelColor(type, x, y, z, gridSize, noise, noiseSeed);
    for (const face of FACES) {
      const nx = x + face.d[0];
      const ny = y + face.d[1];
      const nz = z + face.d[2];
      const neighbor =
        nx >= 0 && nx < gridSize &&
        ny >= 0 && ny < gridSize &&
        nz >= 0 && nz < gridSize
          ? cells.get(microVoxelIndex(nx, ny, nz, gridSize))
          : undefined;
      if (faceHiddenBy(type, neighbor)) continue;
      const base = positions.length / 3;
      for (const corner of face.c) {
        positions.push(
          (x + corner[0]) / gridSize,
          (y + corner[1]) / gridSize,
          (z + corner[2]) / gridSize,
        );
        normals.push(face.n[0], face.n[1], face.n[2]);
        colors.push(red, green, blue, alpha);
      }
      indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
  }

  return makeGeometry(positions, normals, colors, indices);
}

/**
 * Hide a face only when the neighbor fully occludes it within the same
 * render class (opaque / transparent / glowing). Different classes keep
 * the shared face so glow and lit materials can meet cleanly.
 */
function faceHiddenBy(
  self: VoxelMaterial,
  neighbor: VoxelMaterial | undefined,
): boolean {
  if (neighbor === undefined) return false;
  const selfClass = voxelRenderClass(self);
  if (selfClass !== voxelRenderClass(neighbor)) return false;
  if (selfClass === 'transparent') return self === neighbor;
  return true;
}

function buildGreedyGeometry(
  cells: ReadonlyMap<number, VoxelMaterial>,
  gridSize: number,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const mask: Array<GreedyMaskCell | null> = [];
  const x = [0, 0, 0];

  const sample = (coordinates: number[]): VoxelMaterial | undefined => {
    if (coordinates.some((value) => value < 0 || value >= gridSize)) return undefined;
    return cells.get(microVoxelIndex(coordinates[0], coordinates[1], coordinates[2], gridSize));
  };

  for (let d = 0; d < 3; d++) {
    const u = (d + 1) % 3;
    const v = (d + 2) % 3;
    for (x[d] = -1; x[d] < gridSize; x[d]++) {
      let maskIndex = 0;
      for (x[v] = 0; x[v] < gridSize; x[v]++) {
        for (x[u] = 0; x[u] < gridSize; x[u]++) {
          const a = sample(x);
          const next = [...x];
          next[d]++;
          const b = sample(next);
          // Within one opacity class, occupied/empty is enough: the other class
          // was filtered out and therefore behaves like air.
          if (a !== undefined && b !== undefined) {
            if (a === b) {
              mask[maskIndex++] = null;
            } else {
              // Different materials in the same opacity class: keep both faces.
              emitGreedyQuad(
                x, d, u, v, 1, 1, { material: a, normal: 1 }, gridSize,
                positions, normals, colors, indices,
              );
              emitGreedyQuad(
                x, d, u, v, 1, 1, { material: b, normal: -1 }, gridSize,
                positions, normals, colors, indices,
              );
              mask[maskIndex++] = null;
            }
          } else if (a !== undefined) {
            mask[maskIndex++] = { material: a, normal: 1 };
          } else if (b !== undefined) {
            mask[maskIndex++] = { material: b, normal: -1 };
          } else {
            mask[maskIndex++] = null;
          }
        }
      }

      maskIndex = 0;
      for (let j = 0; j < gridSize; j++) {
        for (let i = 0; i < gridSize;) {
          const cell = mask[maskIndex];
          if (!cell) {
            i++;
            maskIndex++;
            continue;
          }
          let width = 1;
          while (
            i + width < gridSize &&
            sameGreedyCell(mask[maskIndex + width], cell)
          ) width++;
          let height = 1;
          heightLoop: while (j + height < gridSize) {
            for (let offset = 0; offset < width; offset++) {
              if (!sameGreedyCell(mask[maskIndex + offset + height * gridSize], cell)) {
                break heightLoop;
              }
            }
            height++;
          }
          x[u] = i;
          x[v] = j;
          emitGreedyQuad(
            x, d, u, v, width, height, cell, gridSize,
            positions, normals, colors, indices,
          );
          for (let row = 0; row < height; row++) {
            for (let column = 0; column < width; column++) {
              mask[maskIndex + column + row * gridSize] = null;
            }
          }
          i += width;
          maskIndex += width;
        }
      }
    }
  }

  return makeGeometry(positions, normals, colors, indices);
}

function emitGreedyQuad(
  x: number[],
  d: number,
  u: number,
  v: number,
  width: number,
  height: number,
  cell: GreedyMaskCell,
  gridSize: number,
  positions: number[],
  normals: number[],
  colors: number[],
  indices: number[],
): void {
  const base = positions.length / 3;
  const point = [...x];
  point[d]++;
  const du = [0, 0, 0];
  const dv = [0, 0, 0];
  du[u] = width;
  dv[v] = height;
  const add = (a: number[], b: number[]) => a.map((value, index) => value + b[index]);
  const corners = cell.normal > 0
    ? [point, add(point, du), add(add(point, du), dv), add(point, dv)]
    : [point, add(point, dv), add(add(point, du), dv), add(point, du)];
  const normal = [0, 0, 0];
  normal[d] = cell.normal;
  const [red, green, blue, alpha] = voxelColor(cell.material);
  for (const corner of corners) {
    positions.push(corner[0] / gridSize, corner[1] / gridSize, corner[2] / gridSize);
    normals.push(normal[0], normal[1], normal[2]);
    colors.push(red, green, blue, alpha);
  }
  indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

function sameGreedyCell(
  candidate: GreedyMaskCell | null | undefined,
  expected: GreedyMaskCell,
): boolean {
  return candidate?.normal === expected.normal &&
    candidate.material === expected.material;
}

function makeGeometry(
  positions: number[],
  normals: number[],
  colors: number[],
  indices: number[],
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 4));
  geometry.setIndex(indices);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function mergeGeometries(parts: MicroVoxelGeometries): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  for (const part of [parts.opaque, parts.transparent, parts.glowing]) {
    const pos = part.getAttribute('position');
    const nor = part.getAttribute('normal');
    const col = part.getAttribute('color');
    const idx = part.getIndex();
    if (!pos || !nor || !col || !idx || pos.count === 0) {
      part.dispose();
      continue;
    }
    const base = positions.length / 3;
    for (let i = 0; i < pos.count; i++) {
      positions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      normals.push(nor.getX(i), nor.getY(i), nor.getZ(i));
      colors.push(col.getX(i), col.getY(i), col.getZ(i), col.getW(i));
    }
    for (let i = 0; i < idx.count; i++) indices.push(base + idx.getX(i));
    part.dispose();
  }
  return makeGeometry(positions, normals, colors, indices);
}

/**
 * Vertex colors must be in Three's working space (linear-sRGB).
 * Passing display hex via THREE.Color applies the same sRGB→linear
 * conversion that MeshLambertMaterial.color uses for solid library cubes.
 */
function voxelColor(
  material: VoxelMaterial,
  x = 0,
  y = 0,
  z = 0,
  gridSize = CRAFT_GRID_SIZE,
  noise: BlockNoiseParams | null = null,
  noiseSeed = 0,
): [number, number, number, number] {
  let r: number;
  let g: number;
  let b: number;
  let alpha: number;
  if (typeof material === 'number') {
    const color = new THREE.Color(blockTypeById(material).color);
    r = color.r;
    g = color.g;
    b = color.b;
    alpha = 1;
  } else {
    const color = new THREE.Color(material.length >= 7 ? material.slice(0, 7) : material);
    r = color.r;
    g = color.g;
    b = color.b;
    alpha = voxelMaterialOpacity(material);
  }
  if (isNoiseActive(noise)) {
    [r, g, b] = applyBlockNoiseRgb(r, g, b, x, y, z, gridSize, noise!, noiseSeed);
  }
  return [r, g, b, alpha];
}
